/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { fasTrashAltIcon } from 'IconLibrary/fontawesome/solid/trash-alt';
import { faTimesIcon } from 'IconLibrary/fontawesome/regular/times';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { ButtonSize, ButtonStyle } from '../../../../aero/aero-button/aero-button.component';
import { TranslationService } from '../../../../common/translation.service';

/**
 * Angular component that will house the modal to delete selected block.
 */
@Component({
  selector: 'bulb-delete-block-dialog',
  templateUrl: './delete-block-dialog.component.html',
  styleUrls: ['./delete-block-dialog.component.scss'],
  standalone: false,
})

export class DeleteBlockComponent implements OnInit {
  /**
   * Button with Grey colour.
   */
  public buttonStyleGrey: ButtonStyle = ButtonStyle.GREY;

  /**
   * Button with Red colour.
   */
  public buttonStyleAlert: ButtonStyle = ButtonStyle.ALERT;

  /**
   * Button size of aero button
   */
  public buttonSize : ButtonSize = ButtonSize.LARGE;

  /**
   * Title of the dialog
   */
  public bodyTitle : any;

  /**
   * Body text of the dialog
   */
  public bodyText : any;

  /**
   * Default Constructor.
   * @param dynamicConfig Dialog configuration.
   * @param dynamicDialogRef Reference to the open dialog.
   * @param svgIconRegistry Icon registry.
   * @param translateService Translate Service.
   */
  constructor(
    private dynamicConfig : DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private svgIconRegistry: SvgIconRegistry,
    private translateService: TranslationService,
  ) {
    this.svgIconRegistry.register([
      faTimesIcon,
      fasTrashAltIcon,
    ]);
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    const contentBlockType = this.dynamicConfig.data.type;
    const { imageCarouselSlide } = this.dynamicConfig.data;
    if (imageCarouselSlide) {
      this.translateService.translate('editor_image_carousel_bar_remove_slide_check_title').then((bodyTitle) => {
        this.bodyTitle = bodyTitle;
        this.translateService.translateCustomKeys('editor_image_carousel_bar_remove_slide_check_text', {})
          .then((bodyText) => {
            this.bodyText = bodyText;
          });
      });
    } else {
      this.translateService.translate(`content_block_type_${contentBlockType}`).then((type) => {
        this.translateService.translateCustomKeys('modal_delete_confirm_title', { type }).then((bodyTitle) => {
          this.bodyTitle = bodyTitle;
        });
        this.translateService.translateCustomKeys('modal_delete_confirm_body', { type }).then((bodyText) => {
          this.bodyText = bodyText;
        });
      });
    }
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    /*
    Important Note:
    This is needed to remove primeng dynamic dialog from the DOM. For some reason, the component that
    registers the initial opening of the dialog can't seem to close it. This only happens in our AngularJS
    framework.
    */
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

  /**
   * Delete selected block and close the dialog.
   */
  deleteBlock() {
    const { imageCarouselSlide } = this.dynamicConfig.data;
    if (imageCarouselSlide) {
      window.dispatchEvent(new CustomEvent('handleCarouselImageDeletion', {
        detail: {
          imageCarouselSlide,
        },
      }));
    } else {
      const deleteSelectedBlockEvent = new CustomEvent(
        'deleteSelectedBlock',
        {
          detail: {
            model: this.dynamicConfig.data.model,
          },
        },
      );
      window.dispatchEvent(deleteSelectedBlockEvent);
    }
    this.closeDialog();
  }
}
